import { useQuery } from '@tanstack/react-query';
import { RouteById } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { PencilIcon } from 'lucide-react';

import { createTableRowSelectionCell, EntityTable } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';

import { EmptyArray, getStudentReviewNotes } from '@/lib/api-calls.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { RegisteredRouteTree, RoutesWithValidConstraint, useNavigateSearchState } from '@/lib/routing.js';

import { useRowsBulkSelection } from '@/lib/bulk-selection.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { useSorting } from '@/lib/sorting.js';
import { useTimeZone } from '@/auth/useTimeZone.js';
import {
  getStudentFeedbackIssueOptions,
  getStudentFeedbackStatusOptions,
  StudentFeedbacksTableRouteConstraint,
} from '@/lib/student-feedback-utils.js';
import { airColumnHelper } from '@/lib/tables.js';

import { StudentFeedbackBulkUpdate } from '@/components/feedbacks/student-feedback-bulk-update.js';
import { TableFilterPlainDateRangePicker } from '@/components/table/table-filter-plain-date-range-picker.js';
import { PeopleBadgeList } from '@/routes/_layout/scheduling/student-groups/-components/people-badge-list.js';
import {
  formatDateWithDayOfWeek,
  formatInstant,
  FrequentTimeZones,
  plainDateFromStringOrElseUndefined,
} from '@airreading/common';
import {
  CLASS_FEEDBACK_REVIEW_TYPE_LABEL,
  GetStudentFeedbacksSortableColumn,
  getStudentFeedbacksSortableColumnSchema,
  SessionFeedbackIssueDetails,
  StudentFeedbackStatus,
  StudentFeedbackIssueLabelMap,
  StudentFeedbackStatusLabelMap,
  StudentReviewNote,
} from '@airreading/common/data';
import { EditStudentFeedbackModal } from './edit-student-feedback-modal.js';
import { ReviewHistoryMetadata } from './review-history-metadata.js';

function reviewNoteId(note: StudentReviewNote): string {
  return note.kind === 'class_feedback' ? note.id : note.feedback.id;
}

const col = airColumnHelper<StudentReviewNote>(getStudentFeedbacksSortableColumnSchema);

export type StudentFeedbacksTableCompatibleRouteId = RoutesWithValidConstraint<StudentFeedbacksTableRouteConstraint>;
export interface StudentFeedbacksTableProps {
  route: RouteById<RegisteredRouteTree, StudentFeedbacksTableCompatibleRouteId>;
  studentId?: string;
}

export function StudentFeedbacksTable({ route, studentId }: StudentFeedbacksTableProps) {
  const { timeZone } = useTimeZone();
  const [sortBy, didChangeSortBy] = useNavigateSearchState(route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetStudentFeedbacksSortableColumn>([sortBy, didChangeSortBy]);

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      studentFeedbackIssues: [
        [selectedStudentFeedbackIssues, activeSelectedStudentFeedbackIssues],
        setSelectedStudentFeedbackIssues,
      ],
      statuses: [[selectedStatuses, activeSelectedStatuses], setSelectedStatuses],
      startDate: [[selectedStartDate], setSelectedStartDate],
      endDate: [[selectedEndDate], setSelectedEndDate],
    },
    resetFilters,
  } = usePaginatedFilters(route.id, ['studentFeedbackIssues', 'statuses', 'startDate', 'endDate']);

  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useQuery({
    ...getStudentReviewNotes(studentId ?? '', {
      pageIndex,
      pageSize,
      filters: {
        studentFeedbackIssues: activeSelectedStudentFeedbackIssues,
        statuses: activeSelectedStatuses,
        startDate: (() => {
          const startPlanDate = plainDateFromStringOrElseUndefined(selectedStartDate);
          return startPlanDate
            ? startPlanDate.toZonedDateTime(FrequentTimeZones.UsCentral).startOfDay().toInstant().toString()
            : undefined;
        })(),
        endDate: (() => {
          const endPlanDate = plainDateFromStringOrElseUndefined(selectedEndDate);
          return endPlanDate
            ? endPlanDate
                .toZonedDateTime(FrequentTimeZones.UsCentral)
                .add({ days: 1 })
                .startOfDay()
                .subtract({ milliseconds: 1 })
                .toInstant()
                .toString()
            : undefined;
        })(),
      },
      sortBy,
    }),
    enabled: !!studentId,
  });

  const reviewNotes = useMemo(() => response?.reviewNotes || EmptyArray, [response]);
  useErrorToast(error, 'Error loading review notes. Please try again.');

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<StudentReviewNote>({
      getRowId: reviewNoteId,
      rowIdToData: (id) => reviewNotes.find((note) => reviewNoteId(note) === id),
    });

  const selectedStudentFeedbacks = rowDataSelection.flatMap((note) =>
    note?.kind === 'student_feedback' ? [note.feedback] : [],
  );

  const onSuccess = () => {
    resetAllRowSelection();
    refetch();
  };

  const handleEditSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const columns = useMemo(
    () => [
      createTableRowSelectionCell(col),
      col.accessor((row) => row, {
        id: 'edit',
        header: '',
        size: 50,
        cell: ({ getValue }) => {
          const note = getValue();
          if (note.kind !== 'student_feedback') return null;
          return (
            <EditStudentFeedbackModal
              studentFeedback={note.feedback}
              trigger={
                <button
                  type="button"
                  aria-label="Edit review note"
                  className="inline-flex cursor-pointer"
                  data-testid={`student-review-note-button-edit-${note.feedback.id}`}
                >
                  <PencilIcon size={16} aria-hidden />
                </button>
              }
              onSuccess={handleEditSuccess}
            />
          );
        },
      }),
      col.accessor((row) => row, {
        id: 'createdAtInstant',
        header: 'Date',
        cell: ({ getValue }) => {
          const note = getValue();
          if (note.kind === 'class_feedback') {
            return formatDateWithDayOfWeek(note.dateInstant, FrequentTimeZones.UsCentral);
          }
          return formatInstant(note.feedback.createdAtInstant, timeZone).formattedDate;
        },
      }),
      col.accessor((row) => row, {
        id: 'reviewer',
        header: 'Reviewer',
        cell: ({ getValue }) => {
          const note = getValue();
          const reviewer = note.kind === 'class_feedback' ? note.reviewer : note.feedback.reviewer;
          return <PeopleBadgeList people={[reviewer]} />;
        },
      }),
      col.accessor((row) => row, {
        id: 'studentFeedbackIssue',
        header: 'Review type',
        cell: ({ getValue }) => {
          const note = getValue();
          return note.kind === 'class_feedback'
            ? CLASS_FEEDBACK_REVIEW_TYPE_LABEL
            : StudentFeedbackIssueLabelMap[note.feedback.studentFeedbackIssue];
        },
      }),
      col.accessor((row) => row, {
        id: 'note',
        header: 'Note',
        meta: {
          getCellClassName: (row) =>
            row.original.kind === 'class_feedback' ? 'max-w-[500px] min-w-0 whitespace-normal align-top' : undefined,
        },
        cell: ({ getValue }) => {
          const note = getValue();
          if (note.kind !== 'class_feedback') return note.feedback.note;
          return (
            <div className="block max-w-[500px] min-w-0 break-words [overflow-wrap:anywhere]">
              <ReviewHistoryMetadata
                studentAttended={note.studentAttended}
                studentIssueLabels={note.studentIssues.map((issue) => SessionFeedbackIssueDetails[issue].label)}
                classNotes={note.classNotes}
                description={note.dailySummary}
              />
            </div>
          );
        },
      }),
      col.accessor((row) => row, {
        id: 'status',
        header: 'Issue status',
        cell: ({ getValue }) => {
          const note = getValue();
          if (note.kind === 'class_feedback') return null;
          return StudentFeedbackStatusLabelMap[note.feedback.status];
        },
      }),
    ],
    [handleEditSuccess, timeZone],
  );

  const table = useReactTable({
    data: reviewNotes,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      sorting,
      rowSelection,
    },
    rowCount: response?.total || 0,
    onRowSelectionChange,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: (row) => row.original.kind === 'student_feedback',
    onPaginationChange,
    manualPagination: true,
    ...tableSortingOptions,
    enableColumnResizing: true,
  });

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterDropdown
          title="Review type"
          options={getStudentFeedbackIssueOptions()}
          selectedValues={selectedStudentFeedbackIssues ?? []}
          onChange={setSelectedStudentFeedbackIssues}
        />
        <TableFilterPlainDateRangePicker
          fromPlainDateString={selectedStartDate}
          toPlainDateString={selectedEndDate}
          onRangeSelect={(range) => {
            setSelectedStartDate(range.fromPlainDateString);
            setSelectedEndDate(range.toPlainDateString);
          }}
          placeholder="Date range"
        />
        <TableFilterDropdown
          title="Issue status"
          options={getStudentFeedbackStatusOptions()}
          selectedValues={selectedStatuses ?? []}
          onChange={setSelectedStatuses}
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      selectedStudentFeedbackIssues,
      setSelectedStudentFeedbackIssues,
      selectedStatuses,
      setSelectedStatuses,
      selectedStartDate,
      selectedEndDate,
      setSelectedStartDate,
      setSelectedEndDate,
      resetFilters,
    ],
  );

  const bulkActionsMenuItems = (
    <>
      <StudentFeedbackBulkUpdate
        confirmMessage="Are you sure you want to change the status to Need review for"
        options={{ studentId, status: StudentFeedbackStatus.need_review }}
        onSuccess={onSuccess}
        studentFeedbacks={selectedStudentFeedbacks}
        title="Need review"
      />
      <StudentFeedbackBulkUpdate
        confirmMessage="Are you sure you want to change the status to Check again for"
        options={{ studentId, status: StudentFeedbackStatus.check_again }}
        onSuccess={onSuccess}
        studentFeedbacks={selectedStudentFeedbacks}
        title="Check again"
      />
      <StudentFeedbackBulkUpdate
        confirmMessage="Are you sure you want to change the status to Resolved for"
        options={{ studentId, status: StudentFeedbackStatus.resolved }}
        onSuccess={onSuccess}
        studentFeedbacks={selectedStudentFeedbacks}
        title="Resolved"
      />
    </>
  );

  return (
    <EntityTable
      table={table}
      showPagination
      isLoading={isLoading}
      hasError={!!error}
      emptyState={'No students found'}
      errorState={<TableErrorState refetchAction={refetch} />}
      filters={{ filterLeftComponents }}
      bulkActionsMenuItems={bulkActionsMenuItems}
      emptyStateClassName="translate-y-0"
      autoManagedColumnIds={['selection', 'edit']}
    />
  );
}
